/**
 * Generate Text (`generate_text`) input / field contract SoT.
 * Defaults are always real strings so the UI never shows Chinese labels
 * while underlying state is still null.
 */

export const GENERATE_TEXT_SKILL_VERSION_DEFAULT = 'v1'
export const GENERATE_TEXT_INSTRUCTION_MAX = 500

export const GENERATE_TEXT_SKILL_TEXT = 'general_text_generation'
export const GENERATE_TEXT_SKILL_GENERAL = 'general_image_interpretation'
export const GENERATE_TEXT_SKILL_OUTFIT = 'outfit_interpretation'

export type GenerateTextLanguage = 'auto' | 'zh-CN' | 'en-US'
/** VistoeraTASK-00594 — AUTO = free length (no platform word-count constraint). */
export type GenerateTextContentLength = 'AUTO' | 'SHORT' | 'MEDIUM' | 'LONG'
export type GenerateTextOutputFormat = 'auto' | 'plain' | 'markdown'

/** New generate_text nodes default to free length (by prompt). */
export const GENERATE_TEXT_CONTENT_LENGTH_NEW_DEFAULT: GenerateTextContentLength = 'AUTO'

export interface GenerateTextInputAsset {
  assetId: string
  assetType: string
  sourceNodeId?: string | null
  displayName?: string | null
  role?: string | null
  /** Optional generation output id when upstream is a generate_* node. */
  generatedOutputId?: string | null
  /** Inline text body for `assetType: 'text'` (Canvas text material context). */
  context?: string | null
}

export interface GenerateTextCurrentResult {
  text: string
  format: GenerateTextOutputFormat | string
  language: string
  title?: string | null
  resultId?: string | null
  runId?: string | null
  skillKey?: string | null
  skillVersion?: string | null
  createdAt?: string | null
  inputFingerprint?: string | null
}

export interface GenerateTextHistoryEntry {
  result: GenerateTextCurrentResult
  version: number
  completedAt?: string | null
}

export function defaultGenerateTextLanguage(locale?: string | null): GenerateTextLanguage {
  const lang = String(locale || '').toLowerCase()
  if (lang.startsWith('en')) return 'en-US'
  return 'zh-CN'
}

export function normalizeGenerateTextLanguage(raw: unknown, locale?: string | null): GenerateTextLanguage {
  const v = typeof raw === 'string' ? raw.trim() : ''
  if (v === 'auto' || v === 'zh-CN' || v === 'en-US') return v
  if (v === 'zh' || v === 'zh_CN' || v === 'zh-Hans') return 'zh-CN'
  if (v === 'en' || v === 'en_US') return 'en-US'
  return defaultGenerateTextLanguage(locale)
}

export function normalizeGenerateTextContentLength(raw: unknown): GenerateTextContentLength {
  const v = typeof raw === 'string' ? raw.trim().toUpperCase() : ''
  if (v === 'AUTO' || v === 'UNRESTRICTED' || v === 'FREE') return 'AUTO'
  if (v === 'SHORT' || v === 'MEDIUM' || v === 'LONG') return v
  if (v === 'DETAILED' || v === 'DETAIL') return 'LONG'
  // Missing / unknown → MEDIUM so historical nodes that relied on the old
  // implicit default keep their prior generation semantics (TASK-00594 §八).
  return 'MEDIUM'
}

export function isGenerateTextContentLengthUnrestricted(
  raw: GenerateTextContentLength | string | null | undefined,
): boolean {
  return normalizeGenerateTextContentLength(raw) === 'AUTO'
}

export function normalizeGenerateTextOutputFormat(raw: unknown): GenerateTextOutputFormat {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : ''
  if (v === 'auto' || v === 'plain' || v === 'markdown') return v
  if (v === 'md' || v === 'text/markdown') return 'markdown'
  if (v === 'text' || v === 'text/plain') return 'plain'
  return 'plain'
}

/** Normalize USER-facing defaults into concrete state (never null). */
export function normalizeGenerateTextFieldDefaults(
  fields: Record<string, unknown>,
  locale?: string | null,
): {
  language: GenerateTextLanguage
  contentLength: GenerateTextContentLength
  outputFormat: GenerateTextOutputFormat
  additionalInstruction: string
  skillVersion: string
} {
  return {
    language: normalizeGenerateTextLanguage(fields.language, locale),
    contentLength: normalizeGenerateTextContentLength(fields.contentLength),
    outputFormat: normalizeGenerateTextOutputFormat(fields.outputFormat),
    additionalInstruction: typeof fields.additionalInstruction === 'string' ? fields.additionalInstruction : '',
    skillVersion:
      typeof fields.skillVersion === 'string' && fields.skillVersion.trim()
        ? fields.skillVersion.trim()
        : GENERATE_TEXT_SKILL_VERSION_DEFAULT,
  }
}

export function inferDefaultGenerateTextSkillKey(sourceNodeType?: string | null): string {
  if (sourceNodeType === 'generate_tryon') return GENERATE_TEXT_SKILL_OUTFIT
  return GENERATE_TEXT_SKILL_GENERAL
}

/** Grapheme-safe truncation for previews (emoji / ZWJ sequences). */
export function truncateGraphemes(text: string, maxGraphemes: number): string {
  if (!text || maxGraphemes <= 0) return ''
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    const parts: string[] = []
    for (const { segment } of segmenter.segment(text)) {
      if (parts.length >= maxGraphemes) {
        return `${parts.join('')}…`
      }
      parts.push(segment)
    }
    return parts.join('')
  }
  const chars = Array.from(text)
  if (chars.length <= maxGraphemes) return text
  return `${chars.slice(0, maxGraphemes).join('')}…`
}
